<template>
  <div
    class="flex justify-center bg-black/75! m-0! items-center outline-none relative w-screen! max-w-[100vw]! phone:p-0 px-[10%] overflow-hidden! h-screen! max-h-screen!"
  >
    <div
      v-click-outside="attachmentClickOutside"
      class="grow flex h-full min-h-0 min-w-0 p-8 phone:p-0"
      @pointerdown.capture="onPointerDown"
    >
      <div
        class="relative flex phone:m-5 w-full min-w-0 max-w-full overflow-x-hidden rounded-8"
      >
        <div
          ref="panelRef"
          data-test="attachment-viewer-panel"
          :class="{
            'rounded-8': !isChatOpen,
            'attachment-document-css-fullscreen': isCssFullscreen,
          }"
          class="rounded-l-8 flex min-h-0 min-w-0 flex-1 flex-col h-full overflow-hidden bg-bg-base-bg1"
          @pointerdown="onPanelPointerDown"
          @pointerup="onPanelPointerUp"
          @pointercancel="onPanelPointerCancel"
        >
          <header
            class="z-20 shrink-0 min-w-0 overflow-hidden bg-bg-base-bg1 rounded-l-8 border-r border-r-bor-nav border-b border-b-bor-nav"
            data-test="attachment-viewer-header"
          >
            <div class="flex items-center gap-4 px-6 py-5 phone:px-4 phone:gap-2">
              <div class="min-w-0 flex-1">
                <p
                  data-test="images-slider-filename"
                  class="text-1316 mb-2 truncate font-medium"
                  :title="headerCurrentFile?.filename ?? ''"
                >
                  {{ headerCurrentFile?.filename }}
                </p>
                <p
                  v-if="headerFiles.length > 1"
                  class="text-1316 text-fg-base-fg22"
                  data-test="attachment-files-count"
                >
                  {{ headerFilesCountText }}
                </p>
              </div>

              <div
                class="flex items-center gap-1"
              >
                <wui-btn
                  v-if="showZoomControls"
                  data-test="images-slider-zoom-in-btn"
                  variant="neut"
                  state="alpha"
                  prepend-icon="zoomPlusM"
                  icon
                  :size="32"
                  @click="viewerActions.zoomIn?.()"
                />
                <wui-btn
                  v-if="showZoomControls"
                  data-test="images-slider-zoom-out-btn"
                  variant="neut"
                  state="alpha"
                  prepend-icon="zoomMinusM"
                  icon
                  :size="32"
                  @click="viewerActions.zoomOut?.()"
                />
                <wui-btn
                  v-if="showRotateControl"
                  data-test="images-slider-rotate-btn"
                  variant="neut"
                  state="alpha"
                  prepend-icon="undoM"
                  icon
                  :size="32"
                  @click="viewerActions.rotate?.()"
                />
                <wui-btn
                  v-if="showFullscreenControl"
                  data-test="document-fullscreen-btn"
                  variant="neut"
                  state="alpha"
                  :prepend-icon="isFullscreen ? 'minimizeM' : 'maximizeM'"
                  icon
                  :size="32"
                  @click="onHeaderToggleFullscreen"
                />
                <download-file-btn
                  :files="headerDownloadFiles"
                  :current-index="headerCurrentIndex"
                />
                <wui-btn
                  v-if="room"
                  data-test="images-slider-chat-btn"
                  variant="neut"
                  state="alpha"
                  prepend-icon="messageM"
                  icon
                  :size="32"
                  @click="toggleChat"
                />
              </div>

              <div class="flex flex-1 shrink-0 justify-end">
                <wui-btn
                  data-test="images-slider-close-btn"
                  variant="neut"
                  state="alpha"
                  prepend-icon="closeRegularMdM"
                  icon
                  :size="32"
                  @click="onHeaderClose"
                />
              </div>
            </div>

            <div
              v-if="headerFiles.length > 1"
              class="min-w-0 w-full px-6 pb-5 phone:px-4"
            >
              <div
                ref="thumbContainerRef"
                class="attachment-viewer-thumbs w-full min-w-0 max-w-full flex gap-x-2 overflow-x-auto snap-x snap-mandatory"
                :class="isDraggingThumbs ? 'cursor-grabbing select-none' : 'cursor-grab'"
                @mousedown.prevent="onThumbsMouseDown"
                @dragstart.prevent
              >
                <button
                  v-for="(file, index) in headerFiles"
                  :key="file.filecookie"
                  type="button"
                  data-test="images-slider-thumbnail"
                  class="h-[52px] w-[80px] shrink-0 snap-start cursor-pointer overflow-hidden rounded-8 border-2 transition-all"
                  :class="index === headerCurrentIndex
                    ? 'border-primary shadow-all-0040-primary-25 opacity-100'
                    : 'border-transparent opacity-50 hover:opacity-75'"
                  @dragstart.prevent
                  @click="onSelectFile(index)"
                >
                  <img
                    v-if="isImageAttachment(file)"
                    draggable="false"
                    :src="getFullURL(`/files/${file.filecookie}`)"
                    :alt="file.filename"
                    class="h-full w-full object-cover pointer-events-none"
                  >
                  <video
                    v-else-if="isVideoAttachment(file)"
                    draggable="false"
                    :src="getFullURL(`/files/${file.filecookie}`)"
                    class="h-full w-full object-cover pointer-events-none"
                    muted
                  />
                  <div
                    v-else
                    class="flex h-full w-full flex-col items-center justify-center bg-bg-base-bg3 px-1"
                  >
                    <span class="text-1012 text-fg-base-fg22 uppercase truncate w-full text-center">
                      {{ getFileExtension(file.filename) }}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </header>
          <div
            data-test="slot-content"
            class="relative z-0 flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <slot />
          </div>
        </div>
        <aside
          v-if="room && isChatOpen && !isTabletViewport"
          class="relative min-w-[375px] rounded-r-8"
        >
          <thread-chat-panel
            :room="room"
            :events="events"
            :event-id-for-quoting="eventRef.eventId"
            :comments-text="getCommentsText"
            class="rounded-r-8"
          />
        </aside>
        <div
          v-if="room && isChatOpen && isTabletViewport"
          class="absolute inset-0 z-30 flex min-h-0 flex-col overflow-hidden rounded-8 bg-bg-base-bg2"
          data-test="thread-chat-overlay"
        >
          <thread-chat-panel
            :room="room"
            :events="events"
            :event-id-for-quoting="eventRef.eventId"
            :comments-text="getCommentsText"
            show-back-button
            panel-class="rounded-8"
            @close="toggleChat"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { closeDialogKey, ClickOutside as vClickOutside, safeInject, WuiBtn } from '@wui/common-library'
import { HistoryMessageItem, IMFile, MessageIncomingEvent, MessageOutgoingEvent, isMessageDeleted } from '@wui/im'
import mitt, { Emitter } from 'mitt'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, onUnmounted, provide, ref, shallowRef, unref, watch } from 'vue'

import { useChatMessageStore } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'


import { DownloadFileBtn } from '@/shared/components/download-file-btn'
import type { AttachmentViewerActions, AttachmentViewerMode } from '@/shared/composables/attachment-viewer'
import { attachmentViewerContextKey, useDocumentFullscreen } from '@/shared/composables/attachment-viewer'
import { useLocalization } from '@/shared/composables/localization'
import { useHorizontalDragScroll } from '@/shared/composables/use-horizontal-drag-scroll'
import { usePreventClickOutsideOnDrag } from '@/shared/composables/use-prevent-click-outside-on-drag'
import { Events } from '@/shared/types/app'
import type { NonEmptyArray } from '@/shared/types/non-empty-array'
import { getTextForDifferentCount } from '@/shared/utils/get-text-for-different-count'
import sort from '@/shared/utils/sort'
import { getFullURL } from '@/shared/utils/url'

import type { AttachmentContentKind } from '../lib/file-type-utils'
import { isImageAttachment, isVideoAttachment } from '../lib/file-type-utils'
import { getEventAttachmentFiles, getViewableFiles } from '../lib/viewable-files'

import ThreadChatPanel from './thread-chat-panel.vue'

const emitter: Emitter<Events> = mitt<Events>()
provide('emitter', emitter)

const props = defineProps<{
  event: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
  filecookie: string
  contentKind?: AttachmentContentKind | null
  files?: IMFile[]
  currentIndex?: number
}>()

const emit = defineEmits<{
  (event: 'select-file', index: number): void
}>()

const panelRef = ref<HTMLElement>()
const event = ref(props.event)
const isChatOpen = ref(false)
const isTabletViewport = ref(false)
const eventRef = event
const { findByPServed: findRoomByPServed } = useStoreRooms()
const { t, tc } = useLocalization()
const { eventIdsByQuotedEventId } = storeToRefs(useChatMessageStore())

const viewerActions = shallowRef<AttachmentViewerActions>({})
const contentRef = ref<HTMLElement>()
const isFullscreen = ref(false)

const {
  toggleFullscreen,
  exitFullscreen,
  isCssFullscreen,
  onPanelPointerDown,
  onPanelPointerUp,
  onPanelPointerCancel,
} = useDocumentFullscreen({ contentRef, panelRef, isFullscreen })

const viewerMode = computed<AttachmentViewerMode>(() => {
  return props.contentKind === 'media' ? 'media' : 'document'
})

const headerFiles = computed(() => {
  if (props.files?.length) {
    return props.files
  }
  return getViewableFiles(getEventAttachmentFiles(event.value, props.filecookie))
})

const headerCurrentIndex = computed(() => {
  if (props.currentIndex !== undefined) {
    return props.currentIndex
  }
  const index = headerFiles.value.findIndex(file => file.filecookie === props.filecookie)
  return index >= 0 ? index : 0
})

const headerCurrentFile = computed(() => headerFiles.value[headerCurrentIndex.value])

const currentFile = computed(() => headerCurrentFile.value
  ?? getEventAttachmentFiles(event.value, props.filecookie)?.find(f => f.filecookie === props.filecookie))

const thumbContainerRef = ref<HTMLElement | null>(null)

const {
  isDragging: isDraggingThumbs,
  onMouseDown: onThumbsMouseDown,
  shouldIgnoreClick: shouldIgnoreThumbClick,
} = useHorizontalDragScroll(thumbContainerRef)

const headerFilesCountText = computed(() => tc('common.filesCount', {
  i: headerCurrentIndex.value + 1,
  n: headerFiles.value.length,
}))

const headerDownloadFiles = computed(() => {
  const files = headerFiles.value.length > 0
    ? headerFiles.value
    : currentFile.value
      ? [currentFile.value]
      : []
  return files as NonEmptyArray<IMFile>
})

const showZoomControls = computed(() => {
  if (viewerMode.value !== 'media' || !headerCurrentFile.value) {
    return false
  }
  if (unref(viewerActions.value.isVideo)) {
    return false
  }
  return isImageAttachment(headerCurrentFile.value)
})

const showRotateControl = computed(() => viewerMode.value === 'media')

const showFullscreenControl = computed(() => viewerMode.value === 'document')

const getFileExtension = (filename: string) => filename.split('.').pop() ?? 'file'

const scrollActiveThumbIntoView = async () => {
  await nextTick()
  const thumbnails = thumbContainerRef.value?.children
  if (!thumbnails?.[headerCurrentIndex.value]) {
    return
  }
  thumbnails[headerCurrentIndex.value]?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  })
}

watch(headerCurrentIndex, () => {
  void scrollActiveThumbIntoView()
}, { immediate: true })

const onHeaderToggleFullscreen = () => {
  void toggleFullscreen()
}

const onHeaderClose = async () => {
  if (isFullscreen.value) {
    await exitFullscreen()
    return
  }
  closeDialog()
}

const onSelectFile = (index: number) => {
  if (shouldIgnoreThumbClick()) {
    return
  }
  emit('select-file', index)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (headerFiles.value.length <= 1) {
    return
  }
  if (event.key === 'ArrowRight' && headerCurrentIndex.value < headerFiles.value.length - 1) {
    onSelectFile(headerCurrentIndex.value + 1)
  }
  if (event.key === 'ArrowLeft' && headerCurrentIndex.value > 0) {
    onSelectFile(headerCurrentIndex.value - 1)
  }
}

provide(attachmentViewerContextKey, {
  mode: viewerMode,
  currentFile,
  isFullscreen,
  viewerActions,
  registerActions: (actions: AttachmentViewerActions) => {
    viewerActions.value = {
      ...viewerActions.value,
      ...actions,
      downloadFiles: actions.downloadFiles ?? computed(() => headerFiles.value),
      downloadIndex: actions.downloadIndex ?? computed(() => headerCurrentIndex.value),
    }
  },
  registerContentRef: (element) => {
    contentRef.value = element.value
  },
})

watch([headerFiles, headerCurrentIndex], () => {
  viewerActions.value = {
    ...viewerActions.value,
    downloadFiles: computed(() => headerFiles.value),
    downloadIndex: computed(() => headerCurrentIndex.value),
  }
}, { immediate: true })

const toggleChat = () => {
  isChatOpen.value = !isChatOpen.value
}

const room = computed(() => findRoomByPServed(event.value.item.pServed))
const closeModal = safeInject(closeDialogKey)
const closeDialog = () => { closeModal(undefined) }
const { onPointerDown, createClickOutsideHandler } = usePreventClickOutsideOnDrag()
const attachmentClickOutside = createClickOutsideHandler(closeDialog)

const eventsIds = computed(() => eventIdsByQuotedEventId.value.get(event.value.eventId) ?? [])
const chatMessageStore = useChatMessageStore()
const events = computed(
  () => [...[...Array.from(eventsIds.value)]
    .map(eventId => chatMessageStore.findById(eventId))]
    .filter(e => !!e)
    .filter(e => !isMessageDeleted(e))
    .sort((a, b) => sort.byTimestamp(b, a)),
)

const getCommentsText = computed(() => {
  const config = {
    forOne: tc('common.comments', 0),
    forTwo: tc('common.comments', 1),
    forFive: tc('common.comments', 2),
  }
  return events.value.length ? getTextForDifferentCount(events.value.length, config) : t('common.noComments')
})

let tabletMediaQuery: MediaQueryList | null = null

const syncTabletViewport = () => {
  isTabletViewport.value = tabletMediaQuery?.matches ?? false
}

onMounted(() => {
  tabletMediaQuery = window.matchMedia('(max-width: 1001px)')
  syncTabletViewport()
  tabletMediaQuery.addEventListener('change', syncTabletViewport)
  window.addEventListener('keydown', handleKeydown)
})

watch(() => props.event, () => {
  event.value = props.event
})

onUnmounted(() => {
  tabletMediaQuery?.removeEventListener('change', syncTabletViewport)
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.attachment-viewer-thumbs {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.attachment-viewer-thumbs::-webkit-scrollbar {
  display: none;
}

.attachment-document-css-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 100;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  border-radius: 0;
}
</style>
